import { useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { confirmDialog } from "@/lib/confirm";
import { Plus, Search, Contact, Pencil, Trash2, Phone } from "lucide-react";

export default function Customers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("customer");
  const [defaultCat, setDefaultCat] = useState("customer");

  const load = () => {
    api.get("/customers", { params: { q: q || undefined } }).then((r) => setList(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  const filtered = list.filter((c) =>
    tab === "personal" ? c.category === "personal" : (c.category || "customer") !== "personal"
  );

  const del = async (c) => {
    if (!(await confirmDialog({ title: `Hapus pelanggan "${c.name}"?` }))) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Pelanggan dihapus");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menghapus pelanggan");
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Data Relasional</p>
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Pelanggan</h1>
        </div>
        <Button data-testid="btn-new-customer" onClick={() => { setEditing(null); setDefaultCat(tab); setOpenForm(true); }} className="gap-2">
          <Plus className="h-4 w-4" /> Tambah {tab === "personal" ? "Pribadi" : "Pelanggan"}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger data-testid="cust-tab-customer" value="customer">Pelanggan</TabsTrigger>
          <TabsTrigger data-testid="cust-tab-personal" value="personal">Pribadi</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="search-customers" placeholder="Cari nama / HP..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
      </div>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <Contact className="h-8 w-8 mx-auto text-muted-foreground/50" />
          <p className="mt-3 text-sm text-muted-foreground">{tab === "personal" ? "Belum ada data pribadi." : "Belum ada pelanggan. Tambahkan pelanggan pertama Anda."}</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {filtered.map((c) => (
            <Card key={c.id} data-testid={`customer-card-${c.id}`} className="p-4 card-hover">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-full bg-primary/10 text-primary grid place-items-center font-bold shrink-0">
                  {(c.name || "?")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold truncate">{c.name}</p>
                    <Badge variant="secondary">{c.category === "personal" ? "Pribadi" : "Customer"}</Badge>
                    {c.wa_verified && <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-200">WA ✓</Badge>}
                  </div>
                  {c.phone && <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phone}</p>}
                  {c.address && <p className="text-xs text-muted-foreground mt-0.5 truncate">{c.address}</p>}
                  <div className="mt-2 flex items-baseline gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Total Utang Aktif</p>
                      <p className={`money text-base font-bold ${c.total_utang > 0 ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground"}`}>{rupiah(c.total_utang || 0)}</p>
                    </div>
                    {c.utang_aktif > 0 && <Badge variant="destructive">{c.utang_aktif} utang</Badge>}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex gap-1.5">
                <Button data-testid={`btn-edit-customer-${c.id}`} size="sm" variant="outline" onClick={() => { setEditing(c); setOpenForm(true); }} className="gap-1 flex-1">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
                <Button data-testid={`btn-del-customer-${c.id}`} size="icon" variant="ghost" onClick={() => del(c)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <CustomerForm
        open={openForm}
        customer={editing}
        defaultCategory={defaultCat}
        onClose={() => setOpenForm(false)}
        onSaved={load}
      />
    </div>
  );
}

export function CustomerForm({ open, customer, defaultCategory = "customer", onClose, onSaved }) {
  const empty = { name: "", phone: "", address: "", category: defaultCategory, notes: "" };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setForm(customer ? { ...empty, ...customer } : { ...empty, category: defaultCategory });
    /* eslint-disable-next-line */
  }, [open, customer, defaultCategory]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name) return toast.error("Isi nama pelanggan");
    setSaving(true);
    try {
      let saved;
      if (customer?.id) {
        await api.patch(`/customers/${customer.id}`, form);
        saved = { ...customer, ...form };
        toast.success("Pelanggan diperbarui");
      } else {
        const r = await api.post("/customers", form);
        saved = r.data;
        toast.success("Pelanggan ditambahkan");
      }
      onSaved?.(saved);
      onClose();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{customer?.id ? "Edit Pelanggan" : "Pelanggan Baru"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Kategori</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="customer-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="customer">Customer / Pelanggan</SelectItem>
                <SelectItem value="personal">Pribadi (mis. Muis, Bang Billah)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Nama</Label><Input data-testid="customer-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Nama lengkap" /></div>
          <div><Label>No HP (WhatsApp)</Label><Input data-testid="customer-phone" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} placeholder="08xxx atau 62xxx" /></div>
          <div><Label>Alamat</Label><Textarea data-testid="customer-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} rows={2} /></div>
          <div><Label>Catatan</Label><Textarea data-testid="customer-notes" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} /></div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Batal</Button>
            <Button data-testid="btn-save-customer" onClick={save} disabled={saving} className="flex-1">Simpan</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
